'use client';

// `<NetworkSyncProvider />` — keeps non-React state in sync with the
// user's network mode. Mounted once at the root.
//
// Two responsibilities:
//
//   1. Mirror `useActiveNetwork()` into the `lib/network/active` module
//      singleton, during render. Non-React utility code (tx-engine connection,
//      Solana config, Turnkey wallet helpers) reads it via
//      `getActiveNetwork()` — they can't subscribe to a hook. Reads the
//      3-state value (preserves `'local'`) so localhost-backed flows
//      see the right origin; the UX layer collapses to 2-state via
//      `useNetworkMode()`.
//
//   2. Hydrate the `NETWORK_PREFERENCE` cookie once on mount so every
//      consumer hook (`useActiveNetwork`, `useUserNetwork`) reads the
//      same store, and the banner + wallet dropdown agree from first
//      paint onward, regardless of which component mounts first.
//
// Cache clearing on network flip is handled by `<SdkV2Provider>`'s
// `key={network}` remount (see `sdk-v2-provider.tsx`) — the entire SDK
// runtime tears down on every flip, discarding the QueryClient with it.
//
// The tx-engine `Connection` singleton is NOT touched directly here —
// it reads `getActiveNetwork()` lazily on every `getConnection()` call
// and rebuilds itself when the network has changed. Importing
// `tx-engine/*` from this provider would transitively pull
// `next/headers` (via the telemetry logger) into the client bundle and
// break the build.

import { useEffect } from 'react';
import { getActiveNetwork, setActiveNetwork } from '@/lib/network/active';
import { useNetworkStore } from '@/lib/network/store';
import { useActiveNetwork } from '@/lib/network/use-active-network';

// Binds the contract SDK to the deployment default before any component
// renders. `active.ts` cannot do this at its own load: it and
// `@/config/solana` import each other.
setActiveNetwork(getActiveNetwork());

export function NetworkSyncProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const network = useActiveNetwork();
  // Rebind during render, not in an effect: children render (and start their
  // queries) before a parent's effect runs, so an effect would let the first
  // render after a switch derive PDAs and ATAs from the previous network.
  // Idempotent, so a repeated or discarded render is harmless.
  if (getActiveNetwork() !== network) setActiveNetwork(network);

  useEffect((): void => {
    useNetworkStore.getState().hydrateFromCookie();
  }, []);

  return <>{children}</>;
}
